import React, { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Check, X, Clock } from 'lucide-react';

const VerificationControls = ({ commitment, currentUser, onVerify, type }) => {
  const [loading, setLoading] = useState({ tl: false, manager: false, dashboard: false });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(null); // 'tl' | 'manager' | null

  const handleVerify = async (verificationType) => {
    setLoading(prev => ({ ...prev, [verificationType]: true }));

    try {
      const token = localStorage.getItem('token');
      const endpoint = verificationType === 'tl'
        ? `/api/pulse-entries/${commitment.id}/verify-commitment-tl`
        : verificationType === 'manager'
        ? `/api/pulse-entries/${commitment.id}/verify-commitment-manager`
        : `/api/pulse-entries/${commitment.id}/verify-commitment-dashboard`;

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Verification failed');
      }

      toast.success(`${verificationType === 'tl' ? 'TL' : verificationType === 'manager' ? 'Manager' : 'Dashboard'} verification successful`);

      // Call callback to refresh commitments
      if (onVerify) {
        onVerify(commitment.id, verificationType);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(prev => ({ ...prev, [verificationType]: false }));
      setConfirmDialogOpen(null);
    }
  };

  const handleCheckboxChange = (verificationType, checked) => {
    if (checked) {
      if (verificationType === 'dashboard') {
        // No confirmation needed for audit trail
        handleVerify(verificationType);
      } else {
        // Show confirmation dialog for TL/Manager
        setConfirmDialogOpen(verificationType);
      }
    }
  };

  // Determine what to show for TL verification
  const renderTLControl = () => {
    if (commitment.commitment_tl_verified) {
      return (
        <div className="flex items-center justify-center" title={`Verified by ${commitment.commitment_tl_verified_by || 'TL'}`}>
          <Check className="w-4 h-4 text-green-600" />
        </div>
      );
    }

    if (currentUser?.role === 'team_leader' || currentUser?.role === 'admin') {
      return (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={false}
            onCheckedChange={(checked) => handleCheckboxChange('tl', checked)}
            disabled={loading.tl}
          />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center" title="Not verified">
        <X className="w-4 h-4 text-gray-400" />
      </div>
    );
  };

  // Determine what to show for Manager verification
  const renderManagerControl = () => {
    if (commitment.commitment_mgr_verified) {
      return (
        <div className="flex items-center justify-center" title={`Verified by ${commitment.commitment_mgr_verified_by || 'Manager'}`}>
          <Check className="w-4 h-4 text-green-600" />
        </div>
      );
    }

    if (currentUser?.role === 'manager' || currentUser?.role === 'admin') {
      const canVerify = commitment.commitment_tl_verified;
      return (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={false}
            onCheckedChange={(checked) => handleCheckboxChange('manager', checked)}
            disabled={loading.manager || !canVerify}
            title={!canVerify ? "TL verification required first" : ""}
          />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center" title="Not verified">
        <X className="w-4 h-4 text-gray-400" />
      </div>
    );
  };

  // Dashboard audit checkbox - available to all users
  const renderDashboardControl = () => {
    if (commitment.commitment_dashboard_verified) {
      return (
        <div className="flex items-center justify-center" title={`Audited by ${commitment.commitment_dashboard_verified_by || 'User'}`}>
          <Check className="w-4 h-4 text-green-600" />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center">
        <Checkbox
          checked={false}
          onCheckedChange={(checked) => handleCheckboxChange('dashboard', checked)}
          disabled={loading.dashboard}
          title="Mark for dashboard audit"
        />
      </div>
    );
  };

  return (
    <>
      {/* Render based on type prop, or all if no type specified */}
      {(!type || type === 'tl') && renderTLControl()}
      {(!type || type === 'manager') && renderManagerControl()}
      {(!type || type === 'audit') && renderDashboardControl()}

      {/* TL Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen === 'tl'} onOpenChange={(open) => !open && setConfirmDialogOpen(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm TL Verification</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to verify this commitment as Team Leader? This action confirms that the commitment data is accurate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleVerify('tl')}>
              Confirm Verification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manager Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen === 'manager'} onOpenChange={(open) => !open && setConfirmDialogOpen(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Manager Verification</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to verify this commitment as Manager? This approves the commitment for fulfillment processing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleVerify('manager')}>
              Confirm Verification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default VerificationControls;
